import React, { useMemo } from 'react';
import { Button } from 'antd';
import {
  useCreateNewSubscriptionCheckoutMutation,
  useGetPricesQuery,
} from '../registrationSlice';
import { useGetUserQuery } from '../../users/usersSlice';
import { SUBSCRIPTION_SIGN_UP_ROUTE } from '../../../app/appConstants';
import { SubscriptionLevel } from './PricingCard';
import { SubscriptionLoading } from '../index';
import { formatAsPounds } from '../../../utils/payment-utils';
import giftBox from '../assets/media/giftBox.svg';
import { RightOutlined } from '@ant-design/icons';
import SparkleSVG from '../assets/media/Sparkle';

function TopUserContent(): React.JSX.Element {
  const [createSession, { isLoading: submitting }] =
    useCreateNewSubscriptionCheckoutMutation();
  const { data: userData } = useGetUserQuery();
  const { data: prices, isLoading: loading } = useGetPricesQuery();
  const { proMonthlyPrice, topUserProMonthlyPrice } = useMemo(
    () => ({
      proMonthlyPrice: prices?.Pro?.find((p) => p.interval === 'month'),
      topUserProMonthlyPrice: prices?.top_user_price?.find(
        (p) => p.interval === 'month'
      ),
    }),
    [prices?.Pro, prices?.top_user_price]
  );
  const requestTopUserCheckoutSessionAndRedirect = () => {
    const successUrl =
      `${window.location.protocol}//${window.location.host}` +
      `${SUBSCRIPTION_SIGN_UP_ROUTE}/pre-success?plan=${encodeURIComponent(
        SubscriptionLevel.Pro
      )}&sessionId={CHECKOUT_SESSION_ID}`;

    const sessionParams = {
      customer_email: userData?.email ?? null,
      price_id: proMonthlyPrice?.id ?? '',
      success_url: successUrl,
      cancel_url: window.location.href,
      is_top_user: true,
    };
    createSession(sessionParams)
      .unwrap()
      .then((redirect_url) => {
        if (redirect_url) {
          window.location.href = redirect_url;
        }
      })
      .catch(() => {
        /* Handled by defaultErrorHandler */
      });
  };

  if (loading) {
    return <SubscriptionLoading hideMessage />;
  }

  return (
    <div className='mx-auto mb-7 mt-5 text-blue-100 '>
      <h2 className='mb-5 font-montserrat text-3xl font-black uppercase text-white'>
        <span className='align-sub leading-[1.5]'>Plus</span>
        <span className='ml-1 mt-[-1em] whitespace-nowrap rounded-full border-[1px] border-white pl-1 pr-1 align-super font-sans text-[0.3em] font-bold uppercase'>
          Pro
        </span>
      </h2>
      <p className='text-3xl font-bold uppercase text-white'>
        Thank you for your continued support!
      </p>
      <p className='mb-6 text-lg font-medium text-white'>
        (Did you know you’re in the top 20% of all users?!)
      </p>
      <img className='mx-auto mb-6' src={giftBox} alt='gift' width={100} />
      <p className='mb-8 text-lg font-medium'>
        Based on this, we’re giving you a{' '}
        <strong className='font-bold text-white'>lifetime discount</strong> on
        our{' '}
        <strong className='font-bold text-white'>
          premium angler subscription
        </strong>
        . Enjoy access to:
      </p>
      <ul className='mx-auto mb-8 max-w-fit list-inside list-image-checkmark-green text-left text-base font-bold leading-8 text-white md:max-w-xl md:columns-2'>
        {[
          'Weekly Giveaways',
          'On-The Bank Insurance',
          'Discounts and Cashback',
          'Brand Perks & Unique Products',
          'Advanced Catch Report Data',
          'and much more...',
        ].map((statement, i) => (
          <li key={`reward-${i}`}>
            <span className='relative left-1 top-[-5px]'>{statement}</span>
          </li>
        ))}
      </ul>
      <div className='mb-9 flex flex-row flex-wrap items-center justify-center gap-x-6 gap-y-2 font-montserrat text-3xl text-white'>
        <span className='font-bold line-through decoration-blue-300'>
          &nbsp;
          {proMonthlyPrice?.cost ? formatAsPounds(proMonthlyPrice.cost) : '--'}
          &nbsp;p/m&nbsp;
        </span>
        <span className='font-black '>
          {topUserProMonthlyPrice?.cost
            ? formatAsPounds(topUserProMonthlyPrice.cost)
            : '--'}
          &nbsp;p/m
          <span className='ml-2.5 whitespace-nowrap rounded-full border-[1px] border-white pb-0.5 pl-2.5 pr-2.5 pt-0.5 align-middle font-sans text-xs font-bold uppercase'>
            your offer
          </span>
        </span>
      </div>
      <div className='bg-bright-gradient mx-auto mb-6 max-w-sm rounded-2xl p-[2px]'>
        <Button
          className='flex h-full w-full flex-row items-center whitespace-normal rounded-[calc(theme(borderRadius.2xl)-2px)] border-none bg-black p-3 text-lg font-normal text-white'
          onClick={() => {
            if (submitting) {
              return; // Don't submit twice
            }
            requestTopUserCheckoutSessionAndRedirect();
          }}
          loading={submitting}
        >
          <div className='flex flex-1 flex-row items-center justify-center'>
            <SparkleSVG className='mr-5 text-3xl' />
            <span className='leading-[1]'>
              Join{' '}
              <strong className='font-montserrat font-black uppercase'>
                Plus
              </strong>{' '}
              Now!
            </span>
            <RightOutlined className='ml-auto' />
          </div>
        </Button>
      </div>
      <p className='mx-auto max-w-sm text-sm font-medium'>
        Ensure to use the same email as linked to your swimbooker account when
        upgrading!
      </p>
    </div>
  );
}

export default TopUserContent;
